// O7 — Unified Inbox page.
// Linear-style triage inbox that aggregates notifications, task assignments
// and @-mentions into a single feed. Supports Unread / Snoozed / Done tabs,
// per-item actions (mark done, snooze, mark unread), optimistic updates and
// live refresh via the App-level SSE stream.

import { useEffect } from "preact/hooks";
import { useSignal, computed } from "@preact/signals";
import { Link, useLocation } from "wouter-preact";
import {
  CalendarClock,
  CheckCheck,
  Clock,
  MailOpen,
  RefreshCw,
} from "lucide-preact";
import { Layout } from "../components/Layout";
import { PageShell } from "../components/ui/PageShell";
import { Button } from "../components/ui/Button";
import { ViewTabs, type ViewTabItem } from "../components/ui/ViewTabs";
import { LoadingSkeleton } from "../components/LoadingSkeleton";
import { usePageHeader } from "../lib/pageHeader";
import { requireLabel } from "../lib/navigation";
import {
  listInbox,
  patchInbox,
  type InboxItem,
  type InboxItemState,
} from "../lib/api";
import { toastError, toastSuccess } from "../lib/toast";
import { refreshInboxCount } from "../lib/inboxBadge";
import { refreshNotifications } from "../store/notifications";

// ---------- pure helpers (exported for unit tests) ----------
// Seit UX-P5 in lib/inboxTime.ts; hier weiter exportiert (Tests, Aufrufer).
export { snoozePresets, relTime, type SnoozePreset } from "../lib/inboxTime";
import { relTime } from "../lib/inboxTime";

import { SourceIcon } from "../components/inbox/SourceIcon";
import { InboxContextList } from "../components/inbox/InboxContextList";
import { uxEnabled } from "../lib/ux";
import { SnoozePicker } from "../components/inbox/SnoozePicker";
import { InboxBulkBar, InboxRowCheckbox } from "../components/inbox/InboxBulkBar";
import {
  inboxKey,
  pruneSelection,
  toggleOne,
  type InboxBulkAction,
} from "../lib/inboxSelection";

// ---------- single inbox item row ----------

interface InboxItemRowProps {
  item: InboxItem;
  onAction: (
    item: InboxItem,
    action: "done" | "unread" | "snooze",
    snoozeUntil?: string
  ) => Promise<void>;
  /** #1399: Kästchen zur Mehrfachauswahl. */
  checked: boolean;
  onToggleSelect: () => void;
}

function InboxItemRow({ item, onAction, checked, onToggleSelect }: InboxItemRowProps): preact.JSX.Element {
  const busy = useSignal(false);
  const showSnooze = useSignal(false);

  const handleDone = async (): Promise<void> => {
    if (busy.value) return;
    busy.value = true;
    try {
      await onAction(item, "done");
    } finally {
      busy.value = false;
    }
  };

  const handleUnread = async (): Promise<void> => {
    if (busy.value) return;
    busy.value = true;
    try {
      await onAction(item, "unread");
    } finally {
      busy.value = false;
    }
  };

  const handleSnooze = async (until: string): Promise<void> => {
    showSnooze.value = false;
    if (busy.value) return;
    busy.value = true;
    try {
      await onAction(item, "snooze", until);
    } finally {
      busy.value = false;
    }
  };

  const isUnread = item.state === "unread";
  // Erledigt, weil die AUFGABE erledigt ist (serverseitig abgeleitet). Dann
  // gibt es hier nichts zurueckzunehmen: der Zustand haengt an der Aufgabe,
  // ein „als ungelesen" wuerde beim naechsten Laden wieder auf erledigt
  // stehen. Statt eines Knopfes, der nichts tut, sagt die Zeile den Grund.
  const doneByTask = item.task_done === true;

  return (
    <li
      class={`group border-b border-[color:var(--color-outline-variant)] last:border-0 transition-colors ${
        isUnread ? "bg-[color:var(--color-primary-container)]/10" : ""
      }`}
    >
      <div class="flex items-start gap-3 px-4 py-3">
        <InboxRowCheckbox checked={checked} title={item.title} onToggle={onToggleSelect} />
        {/* Source icon */}
        <span class="mt-0.5 text-[color:var(--color-on-surface-variant)]">
          <SourceIcon type={item.source_type} />
        </span>

        {/* Content */}
        <div class="flex-1 min-w-0">
          {item.link ? (
            <Link
              href={item.link}
              class="block text-sm font-medium text-[color:var(--color-on-surface)] hover:text-[color:var(--color-primary)] leading-snug truncate focus:outline-none focus:underline"
            >
              {item.title}
            </Link>
          ) : (
            <span class="block text-sm font-medium text-[color:var(--color-on-surface)] leading-snug truncate">
              {item.title}
            </span>
          )}
          <span class="block text-micro text-[color:var(--color-on-surface-variant)] mt-0.5">
            {relTime(item.created_at)}
            {item.state === "snoozed" && item.snooze_until && (
              <span class="ml-2 text-[color:var(--color-warning,#A16207)]">
                · snooze bis {new Date(item.snooze_until).toLocaleDateString("de-DE")}
              </span>
            )}
            {doneByTask && (
              <span class="ml-2 text-[color:var(--color-on-surface-variant)]">
                · Aufgabe erledigt
              </span>
            )}
          </span>
        </div>

        {/* Actions — visible on hover / focus-within */}
        <div
          class="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 transition-opacity shrink-0"
          aria-label="Aktionen"
        >
          {item.state !== "done" && (
            <button
              type="button"
              onClick={() => void handleDone()}
              disabled={busy.value}
              title="Erledigt"
              aria-label="Als erledigt markieren"
              class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors"
            >
              <CheckCheck size={14} aria-hidden="true" />
            </button>
          )}
          {item.state === "done" && !doneByTask && (
            <button
              type="button"
              onClick={() => void handleUnread()}
              disabled={busy.value}
              title="Als ungelesen markieren"
              aria-label="Als ungelesen markieren"
              class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors"
            >
              <MailOpen size={14} aria-hidden="true" />
            </button>
          )}
          {item.state !== "snoozed" && item.state !== "done" && (
            <div class="relative">
              <button
                type="button"
                onClick={() => { showSnooze.value = !showSnooze.value; }}
                disabled={busy.value}
                title="Zurückstellen"
                aria-label="Zurückstellen"
                class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors"
              >
                <Clock size={14} aria-hidden="true" />
              </button>
              {showSnooze.value && (
                <SnoozePicker
                  onPick={(until) => void handleSnooze(until)}
                  onClose={() => { showSnooze.value = false; }}
                />
              )}
            </div>
          )}
        </div>

        {/* Unread dot */}
        {isUnread && (
          <span
            class="size-1.5 mt-2 shrink-0 rounded-full bg-[color:var(--color-primary)]"
            aria-label="ungelesen"
          />
        )}
      </div>
    </li>
  );
}

// ---------- tab type ----------

type InboxTab = InboxItemState;
/** Die Tableiste zeigt zusaetzlich einen Verweis-Tab auf `/follow-ups` — kein
 *  Zustand DIESER Route, deshalb kein Teil von `InboxTab`/`activeTab`. */
type InboxViewTabId = InboxTab | "follow_ups";

const TAB_LABELS: Record<InboxTab, string> = {
  unread: "Ungelesen",
  snoozed: "Zurückgestellt",
  done: "Erledigt",
};

function isInboxTab(id: InboxViewTabId): id is InboxTab {
  return id === "unread" || id === "snoozed" || id === "done";
}

// ---------- main page component ----------

export function Inbox(): preact.JSX.Element {
  const [, setLocation] = useLocation();
  const activeTab = useSignal<InboxTab>("unread");
  const items = useSignal<InboxItem[]>([]);
  const loading = useSignal(false);
  const error = useSignal<string | null>(null);
  // #1399: Mehrfachauswahl (Schlüssel = inboxKey). Gilt nur im aktiven Reiter.
  const selected = useSignal<Set<string>>(new Set());

  const filteredItems = computed(() =>
    items.value.filter((i) => i.state === activeTab.value)
  );

  const loadItems = async (): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const res = await listInbox();
      items.value = res.items;
      selected.value = pruneSelection(selected.value, res.items);
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Unbekannter Fehler";
    } finally {
      loading.value = false;
    }
  };

  // Initial load.
  useEffect(() => {
    void loadItems();
  }, []);

  // Listen for SSE 'notification' events dispatched by App.tsx's global stream.
  // App.tsx fires refreshNotifications(); we additionally listen for a custom
  // dom event so the inbox list also refreshes without its own SSE connection.
  useEffect(() => {
    const handler = (): void => { void loadItems(); };
    window.addEventListener("juuplan:inbox-refresh", handler);
    return () => window.removeEventListener("juuplan:inbox-refresh", handler);
  }, []);

  /**
   * Einen oder mehrere Einträge einordnen: optimistisch umstellen, je Eintrag
   * ein PATCH (höchstens 6 gleichzeitig), fehlgeschlagene einzeln zurückrollen.
   * Einzel-Aktion in der Zeile und Sammelaktion (#1399) teilen diesen Weg.
   */
  const triage = async (
    targets: InboxItem[],
    action: InboxBulkAction,
    snoozeUntil?: string
  ): Promise<void> => {
    if (targets.length === 0) return;
    const newState: InboxItemState =
      action === "done" ? "done" : action === "unread" ? "unread" : "snoozed";
    const keys = new Set(targets.map(inboxKey));
    const before = new Map(
      items.value.filter((i) => keys.has(inboxKey(i))).map((i) => [inboxKey(i), i])
    );

    items.value = items.value.map((i) =>
      keys.has(inboxKey(i))
        ? {
            ...i,
            state: newState,
            snooze_until: snoozeUntil ?? (newState === "snoozed" ? i.snooze_until : null),
          }
        : i
    );
    // Die Einträge verlassen den Reiter — aus der Auswahl nehmen.
    selected.value = new Set([...selected.value].filter((k) => !keys.has(k)));

    const failed = new Set<string>();
    let firstError: unknown = null;
    for (let at = 0; at < targets.length; at += 6) {
      const chunk = targets.slice(at, at + 6);
      const results = await Promise.allSettled(
        chunk.map((t) =>
          patchInbox(t.source_type, t.source_id, {
            state: newState,
            snooze_until: snoozeUntil ?? null,
          })
        )
      );
      results.forEach((r, idx) => {
        if (r.status === "rejected") {
          failed.add(inboxKey(chunk[idx]));
          firstError ??= r.reason;
        }
      });
    }

    if (failed.size > 0) {
      // Nur die fehlgeschlagenen zurückrollen, die anderen sind am Server.
      items.value = items.value.map((i) => {
        const k = inboxKey(i);
        return (failed.has(k) && before.get(k)) || i;
      });
      const err: unknown = firstError;
      toastError(
        targets.length === 1
          ? err instanceof Error ? err.message : "Aktion fehlgeschlagen"
          : `${failed.size} von ${targets.length} Einträgen nicht geändert`
      );
    }
    const ok = targets.length - failed.size;
    if (ok > 0) {
      const what =
        action === "done" ? "als erledigt markiert" : action === "unread" ? "als ungelesen markiert" : "zurückgestellt";
      toastSuccess(
        targets.length === 1
          ? what.charAt(0).toUpperCase() + what.slice(1)
          : `${ok} ${ok === 1 ? "Eintrag" : "Einträge"} ${what}`
      );
      // Keep the unified badge + the bell in sync: triaging a notification-source
      // item mirrors read_at server-side, so the bell must re-pull to reflect it.
      refreshInboxCount();
      void refreshNotifications();
    }
  };

  const handleAction = (
    item: InboxItem,
    action: "done" | "unread" | "snooze",
    snoozeUntil?: string
  ): Promise<void> => triage([item], action, snoozeUntil);

  const toggleSelect = (item: InboxItem): void => {
    selected.value = toggleOne(selected.value, inboxKey(item));
  };

  // Unread count per tab for badges.
  const unreadCount = computed(
    () => items.value.filter((i) => i.state === "unread").length
  );
  const snoozedCount = computed(
    () => items.value.filter((i) => i.state === "snoozed").length
  );

  const badgeOf = (count: number): number | string | undefined =>
    count > 0 ? (count > 99 ? "99+" : count) : undefined;

  const tabItems: ViewTabItem[] = [
    { id: "unread", label: TAB_LABELS.unread, badge: badgeOf(unreadCount.value), panelId: "inbox-tabpanel" },
    { id: "snoozed", label: TAB_LABELS.snoozed, badge: badgeOf(snoozedCount.value), panelId: "inbox-tabpanel" },
    { id: "done", label: TAB_LABELS.done, panelId: "inbox-tabpanel" },
    // Eigene Route (/follow-ups) statt eines vierten Zustands dieser Seite —
    // laut IA-Beschluss gehoert "Nachfragen" als Tab neben die Inbox, die
    // Logik der Follow-ups-Seite bleibt aber unangetastet in FollowUps.tsx.
    { id: "follow_ups", label: "Nachfragen", icon: CalendarClock },
  ];

  const handleTabChange = (id: string): void => {
    if (id === "follow_ups") {
      setLocation("/follow-ups");
      return;
    }
    if (isInboxTab(id as InboxViewTabId)) {
      if (activeTab.value !== id) selected.value = new Set();
      activeTab.value = id as InboxTab;
    }
  };

  usePageHeader({
    title: requireLabel("/inbox"),
    tabs: (
      <ViewTabs items={tabItems} active={activeTab.value} onChange={handleTabChange} label="Inbox-Ansichten" />
    ),
    actions: (
      <Button
        variant="quiet"
        icon={RefreshCw}
        loading={loading.value}
        onClick={() => void loadItems()}
        aria-label="Aktualisieren"
        title="Aktualisieren"
      />
    ),
  });

  // Leerzustand — auch von der P5-Liste genutzt (dort nach dem Ausblenden
  // eigener Aktionen).
  const emptyState = (
          <div class="py-16 text-center">
            {activeTab.value === "unread" ? (
              <>
                <p class="text-2xl mb-2">🎉</p>
                <p class="text-sm font-medium text-[color:var(--color-on-surface)]">
                  Alles erledigt
                </p>
                <p class="text-xs text-[color:var(--color-on-surface-variant)] mt-1">
                  Kein ungelesener Eintrag — gut gemacht!
                </p>
              </>
            ) : (
              <p class="text-sm text-[color:var(--color-on-surface-variant)]">
                Keine Einträge in diesem Tab.
              </p>
            )}
          </div>
  );

  return (
    <Layout>
      {/* narrow: reine Lese-/Triageflaeche wie zuvor (max-w-2xl ~ narrow). */}
      <PageShell width="narrow">
        {/* Error state */}
        {error.value && (
          <div class="rounded border border-[color:var(--color-error)]/30 bg-[color:var(--color-error-container,#FEE2E2)] px-4 py-3 text-sm text-[color:var(--color-on-error-container,#7F1D1D)] mb-4">
            Fehler: {error.value}
          </div>
        )}

        {/* List — the tabpanel the header tablist above controls */}
        <div id="inbox-tabpanel" role="tabpanel" aria-labelledby={`viewtab-${activeTab.value}`}>
        {loading.value && filteredItems.value.length === 0 ? (
          <div class="space-y-2">
            <LoadingSkeleton height={56} />
            <LoadingSkeleton height={56} />
            <LoadingSkeleton height={56} />
          </div>
        ) : uxEnabled("ux_web_p5") ? (
          // UX-Vorschlag P5: Kontext, Gruppierung, Aktionen in der Zeile.
          <InboxContextList
            items={filteredItems.value}
            tabLabel={TAB_LABELS[activeTab.value]}
            onAction={handleAction}
            empty={emptyState}
            tab={activeTab.value}
            selected={selected.value}
            onSelectionChange={(next) => { selected.value = next; }}
            onBulk={triage}
          />
        ) : filteredItems.value.length === 0 ? (
          <div class="py-16 text-center">
            {activeTab.value === "unread" ? (
              <>
                <p class="text-2xl mb-2">🎉</p>
                <p class="text-sm font-medium text-[color:var(--color-on-surface)]">
                  Alles erledigt
                </p>
                <p class="text-xs text-[color:var(--color-on-surface-variant)] mt-1">
                  Kein ungelesener Eintrag — gut gemacht!
                </p>
              </>
            ) : (
              <p class="text-sm text-[color:var(--color-on-surface-variant)]">
                Keine Einträge in diesem Tab.
              </p>
            )}
          </div>
        ) : (
          <>
          <InboxBulkBar
            visible={filteredItems.value}
            tab={activeTab.value}
            selected={selected.value}
            onSelectionChange={(next) => { selected.value = next; }}
            onBulk={triage}
          />
          <ul
            class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] divide-y divide-transparent [&>li:first-child]:rounded-t [&>li:last-child]:rounded-b"
            aria-label={`Inbox — ${TAB_LABELS[activeTab.value]}`}
          >
            {filteredItems.value.map((item) => (
              <InboxItemRow
                key={`${item.source_type}-${item.source_id}`}
                item={item}
                onAction={handleAction}
                checked={selected.value.has(inboxKey(item))}
                onToggleSelect={() => toggleSelect(item)}
              />
            ))}
          </ul>
          </>
        )}
        </div>
      </PageShell>
    </Layout>
  );
}
